import { ArrowLeft } from "lucide-react";
import type { CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils/cn";

interface GlobalBackButtonProps {
  fallbackHref: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * A compact history-aware control for page shells. It only goes back through
 * the in-app history created by React Router; direct links use the supplied
 * fallback instead of sending a visitor somewhere unexpected.
 */
export const GlobalBackButton = ({ fallbackHref, className, style }: GlobalBackButtonProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as { from?: string };
  const historyIndex = typeof window !== "undefined" && typeof window.history.state?.idx === "number"
    ? window.history.state.idx as number
    : 0;

  const handleBack = (): void => {
    if (state.from) {
      navigate(state.from);
      return;
    }
    if (historyIndex > 0) {
      navigate(-1);
      return;
    }
    navigate(fallbackHref);
  };

  return (
    <button
      type="button"
      onClick={handleBack}
      className={cn(
        "fixed z-40 grid h-9 w-9 place-items-center rounded-full border border-border/80 bg-card/95 text-muted-foreground shadow-sm backdrop-blur transition hover:border-primary/40 hover:bg-card hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45",
        className,
      )}
      style={style}
      aria-label="Kthehu"
      title="Kthehu"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
    </button>
  );
};
